import { axInst } from "../../../Components/Login/Service";
import WaterMark from "../../../Components/WaterMark/WaterMark";
import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Loader } from "rsuite";
import { useSelector } from "react-redux";

const dealer_table_details = async (body) => {
  try {
    const res = await axInst.post("offtake/dealer-name-wise-offtake", body);
    const data = res.data;
    const obj = Object.keys(data).map((dealer) => ({
      Dealer: dealer,
      Today: data[dealer].today,
      Mtd: data[dealer].mtd,
      Total: data[dealer].total,
    }));

    const sum = obj.reduce(
      (acc, e) => ({
        today_sum: acc.today_sum + e.Today,
        mtd_sum: acc.mtd_sum + e.Mtd,
        total_sum: acc.total_sum + e.Total,
      }),
      { today_sum: 0, mtd_sum: 0, total_sum: 0 }
    );

    return {
      obj,
      today_sum: sum.today_sum,
      mtd_sum: sum.mtd_sum,
      total_sum: sum.total_sum,
    };
  } catch (error) {
    console.error(error);
    return {
      obj: [],
      today_sum: 0,
      mtd_sum: 0,
      total_sum: 0,
    };
  }
};

const DealerWiseOfftakeTable = () => {
  const { data: filterdata, submitted } = useSelector((e) => e.filtersOfftake);
  const { data, isFetching, isError } = useQuery({
    queryKey: ["dealerTable", submitted],
    queryFn: () => dealer_table_details(filterdata),
    initialData: {
      obj: [],
      today_sum: 0,
      total_sum: 0,
    },
    refetchInterval: 300000,
    refetchIntervalInBackground: true,
    cacheTime: 0,
    staleTime: 0,
  });

  const [sorting, setSorting] = useState([]);
  const [filtering, setFiltering] = useState("");

  const columns = [
    {
      id: "dealer",
      header: "Dealer Name",
      accessorFn: (value) => value.Dealer,
      cell: (info) => <span className="text-left">{info?.getValue()}</span>,
      className: "text-left",
    },
    {
      header: "Today's Offtake",
      accessorFn: (value) => value.Today,
      cell: ({ row }) => (
        <span className="text-right">{row?.original.Today}</span>
      ),
      className: "text-right",
    },
    {
      header: "MTD Offtake",
      accessorFn: (value) => value.Mtd,
      cell: ({ row }) => (
        <span className="text-right">{row?.original.Mtd}</span>
      ),
      className: "text-right",
    },
    {
      header: "Total",
      accessorFn: (value) => value.Total,
      cell: ({ row }) => (
        <span className="text-right w-[70px]">{row?.original.Total}</span>
      ),
      className: "text-right ",
      width: 70,
    },
  ];

  const table = useReactTable({
    data: data?.obj ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting: sorting,
      globalFilter: filtering,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setFiltering,
  });

  return (
    <div className="relative">
      <header className="pb-2 text-base font-semibold text-[#4c4c4c]">
        Dealer Wise Offtake
      </header>
      <div className="self-center overflow-auto h-[14rem]">
        {isError && (
          <div className="grid items-center justify-center w-full h-full">
            <p>Error Occurred</p>
          </div>
        )}
        {isFetching && (
          <div className="grid items-center justify-center w-full h-full">
            <Loader />
          </div>
        )}
        {!isFetching && !isError && !data && (
          <div className="grid items-center justify-center w-full h-full">
            <p>Data Not Available</p>
          </div>
        )}
        {!isFetching && !isError && data && (
          <>
            <WaterMark />
            <table className="w-full">
              <thead className="sticky text-white top-0 bg-[#2A5775] text-left z-[1]">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="p-1 text-sm lg:text-base border text-center border-gray-300 whitespace-nowrap"
                        style={{ width: header.column.columnDef.width }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row, rowIndex) => (
                  <React.Fragment key={row.id}>
                    <tr
                      className={`even:bg-gray-200 odd:bg-white ${
                        rowIndex === 0 ? "cursor-default" : "cursor-pointer"
                      }`}
                    >
                      {row.getVisibleCells().map((cell, cellIndex) => (
                        <td
                          key={cell.id}
                          className="p-1 text-xs text-center lg:text-sm border border-gray-300 h-8 relative"
                          style={{
                            width:
                              cell.column.columnDef.id === "total"
                                ? "70px"
                                : "",
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>

              <tfoot className="sticky bottom-0 bg-[#2A5775] text-white">
                <tr>
                  <td className="py-0.5 px-1 text-xs text-center lg:text-sm border border-gray-300 h-8">
                    Grand Total
                  </td>
                  <td className="py-0.5 px-1 text-xs text-center lg:text-sm border border-gray-300 h-8">
                    {data.today_sum}
                  </td>
                  <td className="py-0.5 px-1 text-xs text-center lg:text-sm border border-gray-300 h-8">
                    {data.mtd_sum}
                  </td>
                  <td className="py-0.5 px-1 text-xs text-center lg:text-sm border border-gray-300 h-8">
                    {data.total_sum}
                  </td>
                </tr>
              </tfoot>
            </table>
          </>
        )}
      </div>
    </div>
  );
};
export default DealerWiseOfftakeTable;
